import { Plus, Search, Trash2, X } from "lucide-react";
import { useCanAccess, useDataProvider, useGetList, useNotify } from "ra-core";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { ClientCard } from "./ClientCard";
import { useClientNotes } from "./notes/useClientNotes";
import { useNoAnswerProgress } from "./noAnswerProgress";
import { ClientSheet } from "./ClientSheet";
import { FilterSelect } from "./FilterSelect";
import {
  LEAD_TEMPERATURES,
  LOST_REASONS,
  PIPELINE_STAGES,
} from "./gulaConstants";
import { fullName, type GulaClient } from "./gulaTypes";
import { KanbanBoard } from "./KanbanBoard";
import { decideLead } from "./leadDecision";
import { LeadDecisionDialog } from "./LeadDecisionDialog";
import { TrashConfirmDialog, TrashListDialog } from "./LeadTrashDialogs";
import { compareLeads } from "./leadOrder";
import { NewLeadDialog } from "./NewLeadDialog";
import { ShiftTabs } from "./ShiftTabs";
import { bucketForClient } from "./callPlan/callPlan";
import { takeCalls, useCallPlan } from "./callPlan/useCallPlan";
import {
  CALLING_STAGES,
  countByShift,
  inShift,
  resolveShift,
  type ShiftKey,
} from "./shifts";
import { StageChangeDialog, type PendingMove } from "./StageChangeDialog";
import { currentCallSlot, useAgents } from "./useAgents";
import { useNow } from "./useNow";

/** Filters live in the URL so Inicio can link to a filtered board. */
const FILTER_KEYS = ["agente", "temp", "zona", "motivo"] as const;
type FilterKey = (typeof FILTER_KEYS)[number];
type Filters = Record<FilterKey, string>;

/**
 * Agent filter values: "mine_free" (my leads and the free ones, what a setter
 * works with), "me", "none" (free), "all", or an agent id.
 */
const passesAgent = (owner: string, agent: string, meId: unknown) => {
  if (agent === "all") return true;
  if (agent === "none") return !owner;
  if (agent === "me") return owner === String(meId);
  if (agent === "mine_free") return !owner || owner === String(meId);
  return owner === agent;
};

const passesFilters = (
  client: GulaClient,
  filters: Filters,
  meId: unknown,
): boolean => {
  const owner = client.sales_id == null ? "" : String(client.sales_id);
  if (!passesAgent(owner, filters.agente, meId)) return false;
  if (filters.temp && client.lead_temperature !== filters.temp) return false;
  if (filters.zona && (client.zone ?? "none") !== filters.zona) return false;
  // Why they did not buy (No compra and Reactivar): to work them by group
  if (filters.motivo && (client.lost_reason ?? "none") !== filters.motivo)
    return false;
  return true;
};

// Top strip of each column, from first call to the outcome
const STAGE_ACCENTS: Record<string, string> = {
  new: "bg-sky-500",
  no_answer: "bg-amber-500",
  first_contact: "bg-violet-500",
  second_contact: "bg-indigo-500",
  recontact: "bg-orange-500",
  sure_purchase: "bg-lime-500",
  no_purchase: "bg-slate-400",
  purchased: "bg-emerald-500",
  reactivation: "bg-fuchsia-500",
};

// Outcomes stay folded as narrow drop zones so the calling columns get the room
const FOLDED_STAGES = ["no_purchase", "purchased", "reactivation"];

/** Setters do not see the leads the admins are reactivating. */
const SETTER_COLUMNS = PIPELINE_STAGES.filter(
  (stage) => stage.value !== "reactivation",
);

// Moves that need extra information before they happen
const STAGES_WITH_DIALOG = [
  "recontact",
  "sure_purchase",
  "no_purchase",
  "purchased",
];

const matches = (client: GulaClient, query: string) => {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return [
    fullName(client),
    client.phone,
    client.email,
    client.city,
    client.zone,
  ]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(needle));
};

export const ClientsPage = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [query, setQuery] = useState("");
  const [pendingMove, setPendingMove] = useState<PendingMove>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { agents, meId, nameOf } = useAgents();
  const { canAccess: isAdmin = false } = useCanAccess({
    resource: "money",
    action: "list",
  });
  const rawFilters = Object.fromEntries(
    FILTER_KEYS.map((key) => [key, searchParams.get(key) ?? ""]),
  ) as Filters;
  // Setters open on their own leads plus the free ones; admins see everything
  const defaultAgent = isAdmin ? "all" : "mine_free";
  const filters: Filters = {
    ...rawFilters,
    agente: rawFilters.agente || defaultAgent,
  };
  const [deciding, setDeciding] = useState<GulaClient | null>(null);
  const [trashing, setTrashing] = useState<GulaClient | null>(null);
  const [isTrashOpen, setTrashOpen] = useState(false);
  const openClientId = searchParams.get("cliente");
  const setParam = (key: string, value: string | null) =>
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  const setOpenClientId = (id: GulaClient["id"] | null) =>
    setParam("cliente", id == null ? null : String(id));
  const hasFilters = FILTER_KEYS.some((key) => rawFilters[key]);
  const currentSlot = currentCallSlot();
  const shift = resolveShift(
    searchParams.get("turno"),
    searchParams.get("franja"),
    currentSlot,
  );
  const setShift = (next: ShiftKey) =>
    setSearchParams(
      (previous) => {
        const params = new URLSearchParams(previous);
        params.set("turno", next);
        params.delete("franja");
        return params;
      },
      { replace: true },
    );
  const [isNewLeadOpen, setNewLeadOpen] = useState(false);
  const { notesOf } = useClientNotes();
  const noAnswerOf = useNoAnswerProgress();
  // Cards show how long each lead has been waiting for a call
  const now = useNow(60_000);

  const {
    data = [],
    refetch,
    isPending,
  } = useGetList<GulaClient>(
    "gula_clients_summary",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "stage_changed_at", order: "DESC" },
    },
    // New leads show up without reloading the page
    { refetchInterval: 60_000 },
  );
  // Search and filters first (they feed the shift counters), then the shift
  const filtered = useMemo(
    () =>
      data
        .filter(
          (client) =>
            matches(client, query) && passesFilters(client, filters, meId),
        )
        .sort(compareLeads(Date.now())),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, query, searchParams, meId, isAdmin],
  );
  const clients = filtered.filter((client) => inShift(client, shift));
  const shiftCounts = countByShift(filtered);
  const openClient =
    data.find((client) => String(client.id) === openClientId) ?? null;
  const zoneOptions = [...new Set(data.map((client) => client.zone ?? "none"))]
    .sort()
    .map((zone) => ({
      value: zone,
      label: zone === "none" ? "Sin zona" : zone,
    }));

  // Who has each lead in their list today
  const { missions, reload: reloadPlan } = useCallPlan();
  const dayOwner = new Map(
    missions.map((mission) => [
      String(mission.client_id),
      String(mission.sales_id),
    ]),
  );

  // "Coger": the lead becomes yours and goes into your list in Mi jornada
  const handleTake = async (client: GulaClient) => {
    if (meId == null) return;
    try {
      const taken = await takeCalls([client.id], meId, bucketForClient(client));
      notify(
        taken
          ? `${client.first_name ?? "Lead"} está en tu jornada`
          : "Ya lo tenía otra persona",
        { type: taken ? "success" : "info" },
      );
      reloadPlan();
      await refetch();
    } catch (error) {
      console.error(error);
      notify("No se ha podido coger", { type: "error" });
    }
  };

  const todayCount = data.filter(
    (client) =>
      new Date(client.created_at).toDateString() === new Date().toDateString(),
  ).length;
  const freeCount = data.filter(
    (client) =>
      client.sales_id == null && CALLING_STAGES.includes(client.stage),
  ).length;
  const overdueCount = data.filter(
    (client) =>
      client.stage === "recontact" &&
      client.recontact_at &&
      new Date(client.recontact_at).getTime() < new Date().setHours(0, 0, 0, 0),
  ).length;

  /** A call nobody picked up: one more attempt, next try scheduled as a task. */
  const handleNoAnswer = async (client: GulaClient) => {
    const attempts = (client.call_attempts ?? 0) + 1;
    try {
      await dataProvider.update("gula_clients", {
        id: client.id,
        data: {
          stage: "no_answer",
          call_attempts: attempts,
          last_call_attempt_at: new Date().toISOString(),
          // "No contesta" leads belong to nobody: anyone can try the next call
        },
        previousData: { id: client.id },
      });
      await dataProvider.create("contact_notes", {
        data: {
          contact_id: client.contact_id,
          text: `Llamada sin respuesta (intento ${attempts}).`,
          date: new Date().toISOString(),
          sales_id: meId ?? null,
        },
      });
      notify(
        `${client.first_name ?? "Lead"}: intento ${attempts} anotado. Queda libre para que cualquiera pruebe en otra franja`,
        { type: "info" },
      );
      await refetch();
    } catch (error) {
      console.error(error);
      notify("No se ha podido anotar la llamada", { type: "error" });
    }
  };

  const handleMove = async (client: GulaClient, toStage: string) => {
    if (toStage === "reactivation") {
      try {
        await decideLead(client, "reactivate");
        notify(`${client.first_name ?? "Lead"} pasa a Reactivar`, {
          type: "info",
        });
        await refetch();
      } catch (error: unknown) {
        notify(
          error instanceof Error ? error.message : "No se ha podido mover",
          { type: "error" },
        );
      }
      return;
    }
    if (toStage === "no_answer") {
      await handleNoAnswer(client);
      return;
    }
    if (STAGES_WITH_DIALOG.includes(toStage)) {
      setPendingMove({ client, toStage });
      return;
    }
    try {
      await dataProvider.update("gula_clients", {
        id: client.id,
        data: { stage: toStage },
        previousData: { id: client.id },
      });
      await refetch();
    } catch (error) {
      console.error(error);
      notify("No se ha podido mover", { type: "error" });
    }
  };

  return (
    <div className="flex flex-col gap-4 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Clientes</h1>
          <p className="text-sm text-muted-foreground">
            {todayCount} leads nuevos hoy
            {freeCount ? ` · ${freeCount} libres para coger` : ""}
            {overdueCount ? ` · ${overdueCount} recontactos vencidos` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar nombre, teléfono, zona…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="w-64 pl-8"
            />
          </div>
          {isAdmin ? (
            <Button
              variant="outline"
              onClick={() => setTrashOpen(true)}
              title="Leads que no lo eran: recuperar"
            >
              <Trash2 className="h-4 w-4" />
              Basura
            </Button>
          ) : null}
          <Button onClick={() => setNewLeadOpen(true)}>
            <Plus className="h-4 w-4" />
            Nuevo lead
          </Button>
        </div>
      </div>

      <ShiftTabs
        value={shift}
        onChange={setShift}
        counts={shiftCounts}
        currentSlot={currentSlot}
      />

      <div className="flex flex-wrap items-center gap-3 px-1">
        <FilterSelect
          label="Agente"
          value={filters.agente}
          onChange={(value) =>
            setParam("agente", value === defaultAgent ? null : value)
          }
          showAll={false}
          options={[
            { value: "mine_free", label: "Míos y libres" },
            { value: "me", label: "Mis leads" },
            { value: "none", label: "Libres para coger" },
            { value: "all", label: "Todos" },
            ...agents.map((agent) => ({
              value: String(agent.id),
              label: nameOf(agent.id),
            })),
          ]}
        />
        <FilterSelect
          label="Temperatura"
          value={filters.temp}
          onChange={(value) => setParam("temp", value)}
          options={LEAD_TEMPERATURES}
          allLabel="Todas"
        />
        <FilterSelect
          label="Zona"
          value={filters.zona}
          onChange={(value) => setParam("zona", value)}
          options={zoneOptions}
          allLabel="Todas"
        />
        {isAdmin ? (
          <FilterSelect
            label="Motivo no compra"
            value={filters.motivo}
            onChange={(value) => setParam("motivo", value)}
            options={[
              ...LOST_REASONS,
              { value: "none", label: "Sin motivo apuntado" },
            ]}
            allLabel="Todos"
          />
        ) : null}
        {hasFilters ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              setSearchParams(
                (previous) => {
                  const next = new URLSearchParams(previous);
                  FILTER_KEYS.forEach((key) => next.delete(key));
                  return next;
                },
                { replace: true },
              )
            }
          >
            <X className="h-4 w-4" />
            Quitar filtros
          </Button>
        ) : null}
        <span className="ml-auto text-sm text-muted-foreground">
          {clients.length} de {data.length}
        </span>
      </div>

      {isPending ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : (
        <KanbanBoard
          columns={isAdmin ? PIPELINE_STAGES : SETTER_COLUMNS}
          items={clients}
          onMove={handleMove}
          accents={STAGE_ACCENTS}
          foldedStages={FOLDED_STAGES}
          renderCard={(client) => (
            <ClientCard
              client={client}
              onOpen={(item) => setOpenClientId(item.id)}
              agentName={nameOf(client.sales_id)}
              onTake={handleTake}
              dayOwnerName={
                dayOwner.has(String(client.id))
                  ? dayOwner.get(String(client.id)) === String(meId)
                    ? "ti"
                    : nameOf(dayOwner.get(String(client.id))).split(" ")[0]
                  : undefined
              }
              meId={meId}
              onNoAnswer={handleNoAnswer}
              notes={notesOf(client.contact_id)}
              noAnswer={noAnswerOf(client.id)}
              releasedFromName={
                nameOf(client.released_from).split(" ")[0] || undefined
              }
              canDecide={
                isAdmin || String(client.sales_id ?? "") === String(meId)
              }
              onDecide={setDeciding}
              onBackToNew={
                isAdmin ? (item) => handleMove(item, "new") : undefined
              }
              onTrash={isAdmin ? setTrashing : undefined}
              now={now}
            />
          )}
        />
      )}

      <LeadDecisionDialog
        client={deciding}
        onClose={() => setDeciding(null)}
        onDone={async () => {
          setDeciding(null);
          await refetch();
        }}
      />
      <TrashConfirmDialog
        client={trashing}
        onClose={() => setTrashing(null)}
        onDone={async () => {
          setTrashing(null);
          await refetch();
        }}
      />
      <TrashListDialog
        open={isTrashOpen}
        onClose={() => setTrashOpen(false)}
        onRestored={() => refetch()}
        nameOf={nameOf}
      />
      <StageChangeDialog
        pending={pendingMove}
        onClose={() => setPendingMove(null)}
        onDone={async () => {
          setPendingMove(null);
          await refetch();
        }}
      />
      <ClientSheet
        client={openClient}
        onClose={() => setOpenClientId(null)}
        onSaved={() => refetch()}
      />
      <NewLeadDialog
        open={isNewLeadOpen}
        onClose={() => setNewLeadOpen(false)}
        onCreated={async () => {
          setNewLeadOpen(false);
          await refetch();
        }}
      />
    </div>
  );
};

ClientsPage.path = "/clientes";
